/**
 * MetaTagManager - Ensures Open Graph and Twitter meta tags reflect current view
 *
 * Notes:
 * - Keep tags in sync at all times so native browser share sheets (iOS/Android/desktop)
 *   pick up the correct preview image and text.
 * - Remote platforms (iMessage, Slack, etc.) will fetch the URL server-side. Our
 *   middleware returns per-image tags for crawler user agents, so remote previews
 *   remain correct as long as the URL is accurate.
 */

function ensureMetaByProperty(propertyName) {
  let el = document.head.querySelector(`meta[property="${propertyName}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', propertyName);
    document.head.appendChild(el);
  }
  return el;
}

function ensureMetaByName(name) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  return el;
}

function setContent(el, value) {
  if (typeof value === 'string') {
    el.setAttribute('content', value);
  } else {
    el.removeAttribute('content');
  }
}

function ensureCanonical(urlString) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', urlString);
}

function removeMetaByProperty(propertyName) {
  const nodes = document.head.querySelectorAll(`meta[property="${propertyName}"]`);
  nodes.forEach(node => node.parentNode?.removeChild(node));
}

function removeMetaByName(name) {
  const nodes = document.head.querySelectorAll(`meta[name="${name}"]`);
  nodes.forEach(node => node.parentNode?.removeChild(node));
}

/**
 * Update social meta tags to match the current view
 * @param {Object} params
 * @param {string} params.siteName
 * @param {string} params.sheetId
 * @param {Object|null} params.imageData - { filename, title, description, altText }
 * @param {string} params.pageUrl
 * @param {string} params.state - 'OVERVIEW' | 'ZOOMED_IN' | 'DETAIL_VIEW'
 */
export function updateSocialMetaTags({ siteName, sheetId, imageData, pageUrl, state, imageWidth, imageHeight }) {
  const isImageState = state === 'ZOOMED_IN' || state === 'DETAIL_VIEW';

  // Common fields
  const defaultSiteTitle = 'Burton Rast Photography';
  const title = isImageState && imageData?.title
    ? `${imageData.title} by Burton Rast`
    : defaultSiteTitle;

  const description = isImageState && imageData?.description
    ? imageData.description
    : 'Unintentionally moody imagery by Burton Rast.';

  const imageUrl = isImageState && imageData?.filename
    ? `/images/${sheetId}/${imageData.filename}`
    : '/images/home-social.jpg';

  const absoluteImageUrl = imageUrl.startsWith('http')
    ? imageUrl
    : `${window.location.origin}${imageUrl}`;

  const imageAlt = isImageState && imageData?.altText ? imageData.altText : undefined;

  // Remove conflicting tags depending on state
  if (isImageState) {
    // Remove all video/player tags so image preview isn't overridden
    ['og:video', 'og:video:url', 'og:video:secure_url', 'og:video:type', 'og:video:width', 'og:video:height']
      .forEach(removeMetaByProperty);
    ['twitter:player', 'twitter:player:width', 'twitter:player:height', 'twitter:player:stream', 'twitter:player:stream:content_type']
      .forEach(removeMetaByName);
  }

  // Open Graph core
  // Always set author metadata
  setContent(ensureMetaByName('author'), 'Burton Rast');
  setContent(ensureMetaByProperty('article:author'), 'Burton Rast');
  setContent(ensureMetaByProperty('og:site_name'), siteName);
  setContent(ensureMetaByProperty('og:title'), title);
  setContent(ensureMetaByProperty('og:description'), description);
  setContent(ensureMetaByProperty('og:url'), pageUrl);

  if (isImageState) {
    // Image share
    setContent(ensureMetaByProperty('og:type'), 'website');
    setContent(ensureMetaByProperty('og:image'), absoluteImageUrl);
    setContent(ensureMetaByProperty('og:image:secure_url'), absoluteImageUrl);
    if (imageWidth) setContent(ensureMetaByProperty('og:image:width'), String(imageWidth));
    if (imageHeight) setContent(ensureMetaByProperty('og:image:height'), String(imageHeight));
    if (imageAlt) setContent(ensureMetaByProperty('og:image:alt'), imageAlt);

    // Twitter
    setContent(ensureMetaByName('twitter:card'), 'summary_large_image');
    setContent(ensureMetaByName('twitter:title'), title);
    setContent(ensureMetaByName('twitter:description'), description);
    setContent(ensureMetaByName('twitter:image'), absoluteImageUrl);
    if (imageAlt) setContent(ensureMetaByName('twitter:image:alt'), imageAlt);
  } else {
    // Overview/home → prefer video player
    setContent(ensureMetaByProperty('og:type'), 'video.other');
    setContent(ensureMetaByProperty('og:image'), absoluteImageUrl);
    setContent(ensureMetaByProperty('og:image:type'), 'image/jpeg');

    const videoUrl = `${window.location.origin}/video/br.mp4`;
    setContent(ensureMetaByProperty('og:video'), videoUrl);
    setContent(ensureMetaByProperty('og:video:url'), videoUrl);
    setContent(ensureMetaByProperty('og:video:secure_url'), videoUrl);
    setContent(ensureMetaByProperty('og:video:type'), 'video/mp4');
    setContent(ensureMetaByProperty('og:video:width'), '1280');
    setContent(ensureMetaByProperty('og:video:height'), '720');

    // Twitter player
    setContent(ensureMetaByName('twitter:card'), 'player');
    setContent(ensureMetaByName('twitter:title'), defaultSiteTitle);
    setContent(ensureMetaByName('twitter:description'), description);
    setContent(ensureMetaByName('twitter:image'), absoluteImageUrl);
    setContent(ensureMetaByName('twitter:player'), videoUrl);
    setContent(ensureMetaByName('twitter:player:width'), '1280');
    setContent(ensureMetaByName('twitter:player:height'), '720');
    setContent(ensureMetaByName('twitter:player:stream'), videoUrl);
    setContent(ensureMetaByName('twitter:player:stream:content_type'), 'video/mp4');
  }

  // Canonical
  ensureCanonical(pageUrl);
}


